import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, Subscription } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { UserRole } from '../../../core/auth/user-role.type';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import {
  ErrorBlockComponent,
  PageContextGuardComponent,
  PageShellComponent,
} from '@clinic-platform/ui-components';
import { TelegramConnectorService } from '../data-access/telegram-connector.service';
import {
  TelegramLinkDto,
  TelegramLinkTokenDto,
  TelegramSettingsDto,
} from '../data-access/telegram-connector.models';

@Component({
  selector: 'app-telegram-connector-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    PageHeaderComponent,
    CompanyContextRequiredComponent,
    ErrorBlockComponent,
    PageContextGuardComponent,
    PageShellComponent,
  ],
  template: `
    <lib-page-shell>
      <app-page-header
        [title]="'telegram.page.title' | t"
        [subtitle]="'telegram.page.subtitle' | t"
      />

      <lib-page-context-guard [blocked]="isCompanyContextRequired">
        <app-company-context-required [message]="'error.settingsCompanyContextRequired'" />
      </lib-page-context-guard>

      @if (!isCompanyContextRequired) {
        @if (canManageTenantSettings) {
          <section class="telegram-page__card">
            <div class="telegram-page__section-head">
              <div>
                <h3>{{ 'telegram.settings.title' | t }}</h3>
                <p>{{ 'telegram.settings.description' | t }}</p>
              </div>
              <span class="telegram-page__status" [class.telegram-page__status--active]="settings?.isActive">
                {{ settings?.isActive ? ('telegram.status.active' | t) : ('telegram.status.inactive' | t) }}
              </span>
            </div>

            @if (isLoadingSettings) {
              <div class="telegram-page__meta">{{ 'common.loading' | t }}</div>
            } @else {
              <dl class="telegram-page__meta-grid">
                <div>
                  <dt>{{ 'telegram.settings.bot' | t }}</dt>
                  <dd>{{ botLabel }}</dd>
                </div>
                <div>
                  <dt>{{ 'telegram.settings.token' | t }}</dt>
                  <dd>{{ settings?.botTokenConfigured ? ('telegram.settings.tokenConfigured' | t) : ('telegram.settings.tokenMissing' | t) }}</dd>
                </div>
                <div>
                  <dt>{{ 'telegram.settings.webhook' | t }}</dt>
                  <dd>{{ isWebhookConfigured ? ('telegram.settings.webhookConfigured' | t) : ('telegram.settings.webhookMissing' | t) }}</dd>
                </div>
                <div>
                  <dt>{{ 'telegram.settings.lastValidated' | t }}</dt>
                  <dd>{{ settings?.lastValidatedAt ? formatDateTime(settings?.lastValidatedAt) : '-' }}</dd>
                </div>
              </dl>
            }

            <form [formGroup]="settingsForm" (ngSubmit)="saveSettings()" class="telegram-page__grid">
              <label class="telegram-page__wide">
                {{ 'telegram.settings.botToken' | t }}
                <input
                  type="password"
                  formControlName="botToken"
                  autocomplete="off"
                  [placeholder]="settings?.botTokenConfigured ? ('telegram.settings.keepExistingToken' | t) : ''"
                />
              </label>

              <label class="telegram-page__checkbox">
                <input type="checkbox" formControlName="isActive" />
                {{ 'telegram.settings.active' | t }}
              </label>

              <div class="telegram-page__actions telegram-page__wide">
                <button type="submit" [disabled]="settingsForm.invalid || isSavingSettings">
                  {{ isSavingSettings ? ('telegram.saving' | t) : ('telegram.settings.save' | t) }}
                </button>
                <button type="button" [disabled]="isValidatingSettings || !settings?.botTokenConfigured" (click)="validateSettings()">
                  {{ isValidatingSettings ? ('telegram.validating' | t) : ('telegram.settings.validate' | t) }}
                </button>
              </div>
            </form>

            <form [formGroup]="webhookForm" (ngSubmit)="configureWebhook()" class="telegram-page__grid telegram-page__subform">
              <label class="telegram-page__wide">
                {{ 'telegram.settings.publicBaseUrlOverride' | t }}
                <input type="url" formControlName="publicBaseUrl" placeholder="https://dev.myhomezone.ru" />
              </label>
              <div class="telegram-page__actions telegram-page__wide">
                <button type="submit" [disabled]="webhookForm.invalid || isConfiguringWebhook || !settings?.botTokenConfigured">
                  {{ isConfiguringWebhook ? ('telegram.configuringWebhook' | t) : ('telegram.settings.configureWebhook' | t) }}
                </button>
              </div>
            </form>

            @if (settings?.webhookUrl) {
              <div class="telegram-page__secret-note">
                {{ 'telegram.settings.webhookSecretNote' | t }}
              </div>
            }

            @if (settingsErrorText) {
              <lib-error-block variant="error" [message]="(settingsErrorText | t) ?? ''" />
            }
            @if (settingsSuccessText) {
              <lib-error-block variant="success" [message]="settingsSuccessText" />
            }
          </section>
        }

        <section class="telegram-page__card">
          <div class="telegram-page__section-head">
            <div>
              <h3>{{ linkSectionTitle | t }}</h3>
              <p>{{ linkSectionDescription | t }}</p>
            </div>
            <span class="telegram-page__status" [class.telegram-page__status--active]="link?.status === 'active'">
              {{ link?.status === 'active' ? ('telegram.link.connected' | t) : ('telegram.link.notConnected' | t) }}
            </span>
          </div>

          @if (isLoadingLink) {
            <div class="telegram-page__meta">{{ 'common.loading' | t }}</div>
          } @else {
            <dl class="telegram-page__meta-grid">
              <div>
                <dt>{{ 'telegram.link.account' | t }}</dt>
                <dd>{{ link?.telegramUsername || '-' }}</dd>
              </div>
              <div>
                <dt>{{ 'telegram.link.linkedAt' | t }}</dt>
                <dd>{{ link?.linkedAt ? formatDateTime(link?.linkedAt) : '-' }}</dd>
              </div>
            </dl>
          }

          @if (linkToken) {
            <div class="telegram-page__link-box">
              <div>
                <strong>{{ 'telegram.link.deepLink' | t }}</strong>
                <p>{{ 'telegram.link.expiresAt' | t }}: {{ formatDateTime(linkToken.expiresAt) }}</p>
              </div>
              <a [href]="linkToken.linkUrl" target="_blank" rel="noopener noreferrer">{{ linkToken.linkUrl }}</a>
            </div>
          }

          <div class="telegram-page__actions">
            <button type="button" (click)="createLinkToken()" [disabled]="isCreatingLinkToken">
              {{ isCreatingLinkToken ? ('telegram.link.preparing' | t) : ('telegram.link.connect' | t) }}
            </button>
            @if (linkToken) {
              <button type="button" (click)="copyLink()" [disabled]="isCopyingLink">
                {{ isCopyingLink ? ('telegram.link.copying' | t) : ('telegram.link.copy' | t) }}
              </button>
            }
            @if (link?.status === 'active') {
              <button type="button" class="telegram-page__button--danger" (click)="revokeLink()" [disabled]="isRevokingLink">
                {{ isRevokingLink ? ('telegram.link.revoking' | t) : ('telegram.link.revoke' | t) }}
              </button>
            }
          </div>

          @if (linkErrorText) {
            <lib-error-block variant="error" [message]="(linkErrorText | t) ?? ''" />
          }
          @if (linkSuccessText) {
            <lib-error-block variant="success" [message]="linkSuccessText" />
          }
        </section>
      }
    </lib-page-shell>
  `,
  styles: [`
    :host {
      display: block;
    }

    .telegram-page__card {
      max-width: 920px;
      margin-bottom: 16px;
      padding: 16px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #ffffff;
      display: grid;
      gap: 14px;
    }

    .telegram-page__section-head {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: flex-start;
    }

    .telegram-page__section-head h3 {
      margin: 0;
      color: #111827;
      font-size: 18px;
    }

    .telegram-page__section-head p,
    .telegram-page__link-box p {
      margin: 4px 0 0;
      color: #6b7280;
      font-size: 14px;
    }

    .telegram-page__status {
      display: inline-flex;
      align-items: center;
      min-height: 28px;
      border: 1px solid #e5e7eb;
      border-radius: 999px;
      padding: 0 10px;
      background: #f9fafb;
      color: #6b7280;
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
    }

    .telegram-page__status--active {
      border-color: #bbf7d0;
      background: #f0fdf4;
      color: #166534;
    }

    .telegram-page__meta,
    .telegram-page__secret-note {
      color: #6b7280;
      font-size: 13px;
    }

    .telegram-page__secret-note {
      padding: 10px 12px;
      border: 1px solid #fde68a;
      border-radius: 8px;
      background: #fffbeb;
      color: #92400e;
    }

    .telegram-page__meta-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 10px;
      margin: 0;
    }

    .telegram-page__meta-grid div {
      min-width: 0;
      padding: 10px;
      border: 1px solid #eef2f7;
      border-radius: 8px;
      background: #f8fafc;
    }

    .telegram-page__meta-grid dt {
      color: #64748b;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .telegram-page__meta-grid dd {
      margin: 4px 0 0;
      color: #0f172a;
      font-size: 14px;
      overflow-wrap: anywhere;
    }

    .telegram-page__grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
      align-items: end;
    }

    .telegram-page__subform {
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
    }

    .telegram-page__wide {
      grid-column: 1 / -1;
    }

    .telegram-page__grid label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      color: #374151;
      font-size: 13px;
      font-weight: 600;
    }

    .telegram-page__grid input[type='password'],
    .telegram-page__grid input[type='url'] {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 10px;
      box-sizing: border-box;
      background: #ffffff;
      color: #111827;
      font-size: 14px;
    }

    .telegram-page__checkbox {
      flex-direction: row !important;
      align-items: center;
      min-height: 40px;
      gap: 8px !important;
      font-weight: 500 !important;
    }

    .telegram-page__actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
    }

    .telegram-page__actions button {
      min-height: 38px;
      border: 1px solid #2563eb;
      border-radius: 8px;
      background: #2563eb;
      color: #ffffff;
      padding: 0 14px;
      cursor: pointer;
    }

    .telegram-page__actions button:disabled {
      opacity: 0.6;
      cursor: default;
    }

    .telegram-page__actions .telegram-page__button--danger {
      border-color: #dc2626;
      background: #ffffff;
      color: #dc2626;
    }

    .telegram-page__link-box {
      display: grid;
      gap: 8px;
      padding: 12px;
      border: 1px solid #dbeafe;
      border-radius: 8px;
      background: #eff6ff;
    }

    .telegram-page__link-box a {
      color: #1d4ed8;
      overflow-wrap: anywhere;
      font-size: 14px;
    }

    @media (max-width: 640px) {
      .telegram-page__card {
        padding: 12px;
      }

      .telegram-page__section-head {
        flex-direction: column;
      }

      .telegram-page__actions,
      .telegram-page__actions button {
        width: 100%;
      }
    }
  `],
})
export class TelegramConnectorPageComponent implements OnInit, OnDestroy {
  private readonly telegramService = inject(TelegramConnectorService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly subscriptions = new Subscription();

  protected readonly settingsForm = this.formBuilder.group({
    botToken: this.formBuilder.nonNullable.control('', [Validators.maxLength(255)]),
    isActive: this.formBuilder.nonNullable.control(false),
  });

  protected readonly webhookForm = this.formBuilder.group({
    publicBaseUrl: this.formBuilder.nonNullable.control('', [Validators.maxLength(2048)]),
  });

  protected settings: TelegramSettingsDto | null = null;
  protected link: TelegramLinkDto | null = null;
  protected linkToken: TelegramLinkTokenDto | null = null;

  protected isLoadingSettings = false;
  protected isSavingSettings = false;
  protected isValidatingSettings = false;
  protected isConfiguringWebhook = false;
  protected isLoadingLink = false;
  protected isCreatingLinkToken = false;
  protected isCopyingLink = false;
  protected isRevokingLink = false;

  protected settingsErrorText: string | null = null;
  protected settingsSuccessText: string | null = null;
  protected linkErrorText: string | null = null;
  protected linkSuccessText: string | null = null;

  public ngOnInit(): void {
    this.load();

    if (this.currentRole === 'system_admin') {
      this.subscriptions.add(
        this.systemAdminContext.companyId$.subscribe(() => this.load()),
      );
      this.subscriptions.add(
        this.systemAdminContext.scopeMode$.subscribe(() => this.load()),
      );
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected get canManageTenantSettings(): boolean {
    return this.currentRole === 'system_admin' || this.currentRole === 'company_admin';
  }

  protected get isClientRole(): boolean {
    return this.currentRole === 'client';
  }

  protected get linkSectionTitle(): string {
    return this.isClientRole ? 'telegram.link.clientTitle' : 'telegram.link.title';
  }

  protected get linkSectionDescription(): string {
    return this.isClientRole ? 'telegram.link.clientDescription' : 'telegram.link.description';
  }

  protected get isWebhookConfigured(): boolean {
    return !!this.settings?.webhookUrl && !!this.settings?.lastWebhookConfiguredAt;
  }

  protected get isCompanyContextRequired(): boolean {
    return this.currentRole === 'system_admin'
      && (this.systemAdminContext.isGlobalScope() || !this.contextCompanyId);
  }

  protected get botLabel(): string {
    if (!this.settings?.botUsername && !this.settings?.botDisplayName) {
      return '-';
    }

    const username = this.settings.botUsername ? `@${this.settings.botUsername}` : '';
    const displayName = this.settings.botDisplayName ?? '';
    return [displayName, username].filter(Boolean).join(' ');
  }

  protected saveSettings(): void {
    if (this.settingsForm.invalid || this.isSavingSettings || !this.canManageTenantSettings || this.isCompanyContextRequired) {
      return;
    }

    const value = this.settingsForm.getRawValue();
    const botToken = value.botToken.trim();
    this.isSavingSettings = true;
    this.settingsErrorText = null;
    this.settingsSuccessText = null;

    this.telegramService.updateSettings(
      {
        ...(botToken ? { botToken } : {}),
        isActive: value.isActive,
      },
      this.contextCompanyId ?? undefined,
    ).pipe(
      finalize(() => {
        this.isSavingSettings = false;
      }),
    ).subscribe({
      next: (settings) => {
        this.applySettings(settings);
        this.settingsForm.controls.botToken.reset('');
        this.settingsSuccessText = 'Telegram settings saved';
      },
      error: (error: unknown) => {
        this.settingsErrorText = this.resolveError(error);
      },
    });
  }

  protected validateSettings(): void {
    if (this.isValidatingSettings || !this.canManageTenantSettings || this.isCompanyContextRequired) {
      return;
    }

    this.isValidatingSettings = true;
    this.settingsErrorText = null;
    this.settingsSuccessText = null;

    this.telegramService.validateSettings(this.contextCompanyId ?? undefined).pipe(
      finalize(() => {
        this.isValidatingSettings = false;
      }),
    ).subscribe({
      next: (settings) => {
        this.applySettings(settings);
        this.settingsSuccessText = 'Telegram bot validated';
      },
      error: (error: unknown) => {
        this.settingsErrorText = this.resolveError(error);
      },
    });
  }

  protected configureWebhook(): void {
    if (this.webhookForm.invalid || this.isConfiguringWebhook || !this.canManageTenantSettings || this.isCompanyContextRequired) {
      return;
    }

    this.isConfiguringWebhook = true;
    this.settingsErrorText = null;
    this.settingsSuccessText = null;

    const publicBaseUrl = this.webhookForm.controls.publicBaseUrl.value.trim();
    this.telegramService.configureWebhook(
      publicBaseUrl ? { publicBaseUrl } : {},
      this.contextCompanyId ?? undefined,
    ).pipe(
      finalize(() => {
        this.isConfiguringWebhook = false;
      }),
    ).subscribe({
      next: (settings) => {
        this.applySettings(settings);
        this.settingsSuccessText = 'Telegram webhook configured';
      },
      error: (error: unknown) => {
        this.settingsErrorText = this.resolveError(error);
      },
    });
  }

  protected createLinkToken(): void {
    if (this.isCreatingLinkToken || this.isCompanyContextRequired) {
      return;
    }

    this.isCreatingLinkToken = true;
    this.linkErrorText = null;
    this.linkSuccessText = null;

    const request$ = this.isClientRole
      ? this.telegramService.createMyClientLinkToken()
      : this.telegramService.createMyLinkToken(this.contextCompanyId ?? undefined);

    request$.pipe(
      finalize(() => {
        this.isCreatingLinkToken = false;
      }),
    ).subscribe({
      next: (token) => {
        this.linkToken = token;
        this.linkSuccessText = 'Open Telegram link and press Start to connect';
      },
      error: (error: unknown) => {
        this.linkErrorText = this.resolveError(error);
      },
    });
  }

  protected revokeLink(): void {
    if (this.isRevokingLink || this.isCompanyContextRequired) {
      return;
    }

    this.isRevokingLink = true;
    this.linkErrorText = null;
    this.linkSuccessText = null;

    const request$ = this.isClientRole
      ? this.telegramService.revokeMyClientLink()
      : this.telegramService.revokeMyLink(this.contextCompanyId ?? undefined);

    request$.pipe(
      finalize(() => {
        this.isRevokingLink = false;
      }),
    ).subscribe({
      next: (link) => {
        this.link = link;
        this.linkToken = null;
        this.linkSuccessText = 'Telegram link revoked';
      },
      error: (error: unknown) => {
        this.linkErrorText = this.resolveError(error);
      },
    });
  }

  protected copyLink(): void {
    const linkUrl = this.linkToken?.linkUrl;
    if (!linkUrl || this.isCopyingLink) {
      return;
    }

    this.isCopyingLink = true;
    this.linkErrorText = null;
    this.linkSuccessText = null;

    if (!navigator.clipboard) {
      this.linkErrorText = 'error.generic';
      this.isCopyingLink = false;
      return;
    }

    void navigator.clipboard.writeText(linkUrl)
      .then(() => {
        this.linkSuccessText = 'Telegram link copied';
      })
      .catch(() => {
        this.linkErrorText = 'error.generic';
      })
      .finally(() => {
        this.isCopyingLink = false;
      });
  }

  protected formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    return new Date(value).toLocaleString();
  }

  private load(): void {
    this.settingsErrorText = null;
    this.linkErrorText = null;
    this.settingsSuccessText = null;
    this.linkSuccessText = null;
    this.linkToken = null;

    if (this.isCompanyContextRequired) {
      this.settings = null;
      this.link = null;
      return;
    }

    if (this.canManageTenantSettings) {
      this.loadSettings();
    }

    this.loadLink();
  }

  private loadSettings(): void {
    this.isLoadingSettings = true;
    this.telegramService.getSettings(this.contextCompanyId ?? undefined).pipe(
      finalize(() => {
        this.isLoadingSettings = false;
      }),
    ).subscribe({
      next: (settings) => this.applySettings(settings),
      error: (error: unknown) => {
        this.settingsErrorText = this.resolveError(error);
      },
    });
  }

  private loadLink(): void {
    this.isLoadingLink = true;
    const request$ = this.isClientRole
      ? this.telegramService.getMyClientLink()
      : this.telegramService.getMyLink(this.contextCompanyId ?? undefined);

    request$.pipe(
      finalize(() => {
        this.isLoadingLink = false;
      }),
    ).subscribe({
      next: (link) => {
        this.link = link;
      },
      error: (error: unknown) => {
        this.linkErrorText = this.resolveError(error);
      },
    });
  }

  private applySettings(settings: TelegramSettingsDto | null): void {
    this.settings = settings;
    this.settingsForm.patchValue({
      botToken: '',
      isActive: settings?.isActive ?? false,
    });
  }

  private get contextCompanyId(): string | null {
    if (this.currentRole === 'system_admin') {
      return this.systemAdminContext.getCompanyId();
    }

    return null;
  }

  private get currentRole(): UserRole | null {
    return this.authService.getCurrentRole() as UserRole | null;
  }

  private resolveError(error: unknown): string {
    return resolveHttpErrorMessageKey(error, {
      fallback: 'error.generic',
      forbidden: 'error.forbiddenResource',
      badRequestByErrorCode: {
        telegram_webhook_not_configured: 'telegram.error.webhookNotConfigured',
      },
    });
  }
}
